import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PDFDownloadLink } from '@react-pdf/renderer';
import ModalShell from '@/components/ModalShell';
import Avatar from '@/components/Avatar';
import StatusPill from '@/components/StatusPill';
import { I } from '@/lib/icons';
import { fmtEur, fmtDate } from '@/lib/format';
import { useWorkspace, useInvoices } from '@/hooks/useData';
import { useCloseOnWorkspaceSwitch } from '@/contexts/WorkspaceContext';
import { useToast } from '@/components/Toast';
import InvoicePDF from '@/components/InvoicePDF';

export default function InvoiceDetailModal({ open, onClose, invoice }) {
  const navigate = useNavigate();
  const { workspace } = useWorkspace();
  const { update, remove } = useInvoices(workspace?.id);
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  useCloseOnWorkspaceSwitch(open, onClose);

  if (!open || !invoice) return null;
  const client = invoice.client;
  const items = Array.isArray(invoice.items) ? invoice.items : [];

  const markPaid = async () => {
    setBusy(true);
    try {
      await update(invoice.id, { status: 'paid', paid_at: new Date().toISOString().slice(0, 10) });
      toast.success('Označené ako zaplatené.');
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const onDelete = async () => {
    if (!window.confirm(`Naozaj odstrániť faktúru ${invoice.number}?`)) return;
    setBusy(true);
    try { await remove(invoice.id); toast.success('Vymazané.'); onClose(); }
    catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const onEdit = () => { onClose(); navigate(`/faktury/${invoice.id}/upravit`); };

  return (
    <ModalShell open={open} onClose={onClose} title={`Faktúra ${invoice.number}`}
      maxWidth={680}
      rightAction={
        <div style={{ display: 'flex', gap: 6 }}>
          <PDFDownloadLink
            document={<InvoicePDF invoice={invoice} workspace={workspace} client={client} />}
            fileName={`FA-${invoice.number}.pdf`}
            className="cd-btn cd-btn-secondary cd-btn-sm"
            style={{ textDecoration: 'none' }}
          >
            {({ loading }) => (
              <span data-testid="pdf-download" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                {loading ? <><span className="cd-spinner" /> PDF...</> : <><I.download /> PDF</>}
              </span>
            )}
          </PDFDownloadLink>
          <button onClick={onEdit} className="cd-btn cd-btn-secondary cd-btn-sm"
            data-testid="edit-invoice"><I.pencil /> Upraviť</button>
        </div>
      }>
      {/* Header: client + amount */}
      <div style={{ padding: '20px 22px', display: 'flex', alignItems: 'center', gap: 16,
        borderBottom: '1px solid var(--border)' }}>
        <Avatar name={client?.name || '?'} size={56} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>{client?.name || 'Bez klienta'}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {client?.ico ? `IČO ${client.ico}` : ''}
            {client?.dic ? ` · DIČ ${client.dic}` : ''}
          </div>
          <div style={{ marginTop: 6 }}><StatusPill status={invoice.status} invoice={invoice} /></div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{
            fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase',
            letterSpacing: '0.06em', fontWeight: 700, marginBottom: 2,
          }}>Spolu</div>
          <div style={{
            fontSize: 30, fontWeight: 700, fontFamily: 'var(--font-display)',
            letterSpacing: '-0.02em',
          }}>{fmtEur(invoice.total)}</div>
        </div>
      </div>

      {/* Meta */}
      <div style={{ padding: '16px 22px',
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14,
        borderBottom: '1px solid var(--border)' }}>
        <Meta label="Vystavená" value={fmtDate(invoice.issue_date)} />
        <Meta label="Splatnosť" value={fmtDate(invoice.due_date)} />
        <Meta label="VS" value={invoice.variable_symbol || invoice.number} mono />
        <Meta label="Zaplatená" value={invoice.paid_at ? fmtDate(invoice.paid_at) : '—'} />
      </div>

      {/* Items */}
      <div style={{ padding: '16px 22px' }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: 'var(--text-muted)',
          textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8,
        }}>Položky</div>
        <div style={{ background: 'var(--bg-input)', border: '1px solid var(--border)',
          borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
          {items.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>Žiadne položky.</div>
          ) : items.map((it, idx) => (
            <div key={idx} style={{
              display: 'grid', gridTemplateColumns: '1fr 70px 90px 110px',
              gap: 10, padding: '10px 14px', alignItems: 'center',
              borderBottom: idx < items.length - 1 ? '1px solid var(--border)' : 'none',
              fontSize: 13,
            }}>
              <div>{it.desc}</div>
              <div className="tabnum" style={{ color: 'var(--text-muted)' }}>{it.qty} {it.unit || 'ks'}</div>
              <div className="tabnum" style={{ textAlign: 'right', color: 'var(--text-muted)' }}>{fmtEur(it.price)}</div>
              <div className="tabnum" style={{ textAlign: 'right', fontWeight: 600 }}>{fmtEur((Number(it.qty) || 0) * (Number(it.price) || 0))}</div>
            </div>
          ))}
        </div>
        {invoice.notes && (
          <div style={{ marginTop: 14, fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>
            {invoice.notes}
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div style={{ padding: '14px 22px 20px', display: 'flex', gap: 8, justifyContent: 'space-between',
        borderTop: '1px solid var(--border)' }}>
        <button onClick={onDelete} disabled={busy}
          data-testid="delete-invoice"
          className="cd-btn cd-btn-ghost"
          style={{ color: 'var(--danger)' }}><I.trash /> Odstrániť</button>
        {invoice.status !== 'paid' && (
          <button onClick={markPaid} disabled={busy}
            data-testid="mark-paid" className="cd-btn cd-btn-primary">
            <I.check /> Označiť ako zaplatenú
          </button>
        )}
      </div>
    </ModalShell>
  );
}

function Meta({ label, value, mono }) {
  return (
    <div>
      <div style={{
        fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase',
        letterSpacing: '0.07em', fontWeight: 700, marginBottom: 3,
      }}>{label}</div>
      <div style={{ fontSize: 13, fontFamily: mono ? 'var(--font-mono)' : 'inherit', fontWeight: 500 }}>{value}</div>
    </div>
  );
}
